import React, { useEffect, useState } from 'react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, Sparkles, FileDown, Save } from 'lucide-react';
import { toast } from 'sonner';
import { needsAnalysisAPI } from '@/lib/api';

export default function NeedsAnalysisDrawer({ open, onOpenChange, leadId, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [data, setData] = useState(null);
  const [sets, setSets] = useState(null);
  const [productKey, setProductKey] = useState('generic');
  const [language, setLanguage] = useState('en');
  const [answers, setAnswers] = useState({});

  const loadFor = () => {
    setLoading(true);
    Promise.all([needsAnalysisAPI.getForLead(leadId), needsAnalysisAPI.getQuestionsConfig()])
      .then(([lr, cr]) => {
        const d = lr.data;
        setData(d);
        setSets(cr.data.sets || {});
        setProductKey(d.product_key);
        setLanguage(d.language);
        const map = {};
        (d.answers || []).forEach((a) => { map[a.id] = a.answer || ''; });
        setAnswers(map);
      })
      .catch(() => toast.error('Could not load the needs analysis'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open && leadId) loadFor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, leadId]);

  // Resolve the visible questions locally from the config for the selected
  // product + language. No network call on switch, so in-progress answers
  // are never discarded until the user explicitly clicks Save.
  const lang = String(language || 'en').toLowerCase().startsWith('es') ? 'es' : 'en';
  const availableProducts = sets
    ? Object.keys(sets).map((k) => ({ key: k, label: (sets[k].label || {})[lang] || (sets[k].label || {}).en || k }))
    : (data?.available_products || []);
  const questions = sets && sets[productKey]
    ? (sets[productKey].questions || []).map((q) => ({
        id: q.id, type: q.type || 'text',
        label: (q.label || {})[lang] || (q.label || {}).en || q.id,
      }))
    : (data?.questions || []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await needsAnalysisAPI.saveForLead(leadId, {
        product_key: productKey, language,
        answers: questions.map((q) => ({ id: q.id, answer: answers[q.id] || '' })),
      });
      setData((prev) => ({ ...prev, ...r.data }));
      toast.success('Needs analysis saved');
      onSaved?.();
    } catch {
      toast.error('Could not save the needs analysis');
    } finally {
      setSaving(false);
    }
  };

  const handleAi = async () => {
    setAiBusy(true);
    try {
      await handleSave();
      const r = await needsAnalysisAPI.aiSummary(leadId);
      setData((prev) => ({ ...prev, ai_summary: r.data.ai_summary, cross_sell: r.data.cross_sell || [] }));
      toast.success('AI recommendation generated');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate the AI recommendation');
    } finally {
      setAiBusy(false);
    }
  };

  const handlePdf = async () => {
    try {
      const r = await needsAnalysisAPI.downloadPdf(leadId);
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'needs-analysis.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not export the PDF');
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="needs-analysis-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 font-heading">
            <Sparkles size={18} /> Needs Analysis
          </SheetTitle>
          <SheetDescription>
            Capture what the lead needs. Questions adapt to the product and the lead's language.
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400">
            <Loader2 className="animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Product</Label>
                <Select
                  value={productKey}
                  onValueChange={(v) => setProductKey(v)}
                >
                  <SelectTrigger data-testid="na-product-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {availableProducts.map((p) => (
                      <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Language</Label>
                <Select
                  value={language}
                  onValueChange={(v) => setLanguage(v)}
                >
                  <SelectTrigger data-testid="na-language-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="es">Español</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {questions.map((q) => (
              <div key={q.id} data-testid={`na-q-${q.id}`}>
                <Label className="text-sm">{q.label}</Label>
                {q.type === 'boolean' ? (
                  <div className="flex items-center gap-2 mt-1">
                    <Switch
                      checked={String(answers[q.id] || '').toLowerCase() === 'yes'}
                      onCheckedChange={(c) => setAnswers((p) => ({ ...p, [q.id]: c ? 'Yes' : 'No' }))}
                      data-testid={`na-input-${q.id}`}
                    />
                    <span className="text-sm text-slate-500">{answers[q.id] || 'No'}</span>
                  </div>
                ) : q.type === 'textarea' ? (
                  <Textarea
                    value={answers[q.id] || ''}
                    onChange={(e) => setAnswers((p) => ({ ...p, [q.id]: e.target.value }))}
                    rows={2}
                    data-testid={`na-input-${q.id}`}
                  />
                ) : (
                  <Input
                    value={answers[q.id] || ''}
                    onChange={(e) => setAnswers((p) => ({ ...p, [q.id]: e.target.value }))}
                    data-testid={`na-input-${q.id}`}
                  />
                )}
              </div>
            ))}

            {data?.ai_summary && (
              <div className="rounded-md border border-indigo-200 bg-indigo-50 p-3" data-testid="na-ai-summary">
                <p className="text-xs font-semibold text-indigo-900 mb-1 flex items-center gap-1">
                  <Sparkles size={12} /> AI Recommendation
                </p>
                <p className="text-sm text-indigo-900 whitespace-pre-wrap">{data.ai_summary}</p>
                {(data.cross_sell || []).length > 0 && (
                  <div className="mt-2 pt-2 border-t border-indigo-200" data-testid="na-cross-sell">
                    <p className="text-[11px] font-semibold text-indigo-900 mb-1">Cross-sell opportunities</p>
                    <div className="flex flex-wrap gap-1">
                      {data.cross_sell.map((p) => (
                        <span key={p} className="text-[11px] bg-amber-100 text-amber-800 rounded-full px-2 py-0.5">{p}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-2 pt-2">
              <Button onClick={handleSave} disabled={saving} data-testid="na-save-btn">
                {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />}
                Save
              </Button>
              <Button variant="outline" onClick={handleAi} disabled={aiBusy} data-testid="na-ai-btn">
                {aiBusy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Sparkles size={14} className="mr-1" />}
                AI Recommendation
              </Button>
              <Button variant="outline" onClick={handlePdf} data-testid="na-pdf-btn">
                <FileDown size={14} className="mr-1" /> PDF
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
